import { Box } from '@chakra-ui/react';
import React from 'react';

export type PhotoAnnotatorRef = {
  onClear: () => void;
  onSave: () => Promise<string | null>;
};

type PhotoAnnotatorCanvasProps = {
  photoSrc: string;
  color: string;
};

type Point = { x: number; y: number };

const PhotoAnnotatorCanvasComponent: React.ForwardRefRenderFunction<
  PhotoAnnotatorRef,
  PhotoAnnotatorCanvasProps
> = ({ photoSrc, color }, selfRef) => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const isDrawingRef = React.useRef(false);
  const hasDrawnRef = React.useRef(false);
  const lastPointRef = React.useRef<Point | null>(null);

  const drawBackground = React.useCallback(
    (onDone?: () => void) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;

      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        ctx.drawImage(image, 0, 0);
        hasDrawnRef.current = false;
        onDone?.();
      };
      image.src = photoSrc;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [photoSrc],
  );

  React.useEffect(() => {
    drawBackground();
  }, [drawBackground]);

  const getCanvasPoint = React.useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>): Point | null => {
      const canvas = canvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    },
    [],
  );

  const handlePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      canvasRef.current?.setPointerCapture(event.pointerId);
      isDrawingRef.current = true;
      lastPointRef.current = getCanvasPoint(event);
    },
    [getCanvasPoint],
  );

  const handlePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      const point = getCanvasPoint(event);
      if (!canvas || !ctx || !point || !lastPointRef.current) return;

      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(canvas.width, canvas.height) * 0.006 + 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();

      lastPointRef.current = point;
      hasDrawnRef.current = true;
    },
    [color, getCanvasPoint],
  );

  const handlePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (canvas?.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
      isDrawingRef.current = false;
      lastPointRef.current = null;
    },
    [],
  );

  const handleClear = React.useCallback(() => {
    drawBackground();
  }, [drawBackground]);

  const handleSave = React.useCallback(async () => {
    if (!hasDrawnRef.current) return null;
    return canvasRef.current?.toDataURL('image/jpeg', 0.92) ?? null;
  }, []);

  React.useImperativeHandle(selfRef, () => ({
    onClear: handleClear,
    onSave: handleSave,
  }));

  return (
    <Box
      display="flex"
      w="full"
      flex="1"
      justifyContent="center"
      alignItems="center"
      bg="gray.900"
      borderRadius="md"
      overflow="hidden"
    >
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          maxWidth: '100%',
          maxHeight: '70vh',
          touchAction: 'none',
          cursor: 'crosshair',
        }}
      />
    </Box>
  );
};

export default React.forwardRef(PhotoAnnotatorCanvasComponent);
